import React, { useEffect, useState, useRef } from 'react';
import { GoogleMap, Marker, StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api';
import {
  CircularProgress,
  Box,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';

const libraries = ['places'];

const LocationPickerMap = ({
  open,
  onClose,
  locationType,
  selectedLocationFromForm,
  onSelectLocation
}) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const searchBoxRef = useRef(null);
  const mapRef = useRef(null);

  const [searchLoading, setSearchLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);

  const [searchInput, setSearchInput] = useState(''); // <-- Add this state

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  let debounceTimer;
  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setActiveIndex(-1);

    clearTimeout(debounceTimer);

    if (!value) {
      setSearchResults([]);
      return;
    }

    debounceTimer = setTimeout(() => {
      setSearchLoading(true);
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions({ input: value }, (predictions, status) => {
        setSearchLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setSearchResults(predictions);
        } else {
          setSearchResults([]);
        }
      });
    }, 400); // 400ms debounce
  };

  // Initial setup
  useEffect(() => {
    if (open) {
      if (selectedLocationFromForm?.lat && selectedLocationFromForm?.lon) {
        setSelectedLocation({
          lat: Number(selectedLocationFromForm.lat),
          lng: Number(selectedLocationFromForm.lon)
        });
        setLoading(false);
      } else {
        getCurrentLocation();
      }
    }
  }, [open, selectedLocationFromForm]);

  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setCurrentLocation({ lat: latitude, lng: longitude });
          setSelectedLocation({ lat: latitude, lng: longitude });
          setLoading(false);
        },
        (err) => {
          console.warn('Geolocation error:', err);
          const fallback = { lat: 24.8607, lng: 67.0011 };
          setCurrentLocation(fallback);
          setSelectedLocation(fallback);
          setLoading(false);
        }
      );
    } else {
      const fallback = { lat: 24.8607, lng: 67.0011 };
      setCurrentLocation(fallback);
      setSelectedLocation(fallback);
      setLoading(false);
    }
  };

  // Fetch location details using Google Geocoder
  const getLocationDetails = (lat, lng, shouldSave = false) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const addressComponents = results[0].address_components;
        const formattedAddress = results[0].formatted_address;

        let city = '',
          country = '',
          state = '',
          zone = '';

        addressComponents.forEach((component) => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('country')) {
            country = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.short_name;
          }
          if (component.types.includes('sublocality_level_1')) {
            zone = component.short_name;
          }
        });

        const locationData = {
          address: formattedAddress,
          state,
          city,
          zone,
          country,
          lat,
          lon: lng
        };

        // Update marker
        setSelectedLocation({ lat, lng });

        // Update input field instantly
        setSearchInput(formattedAddress);

        // Save on button click only
        if (shouldSave) {
          onSelectLocation(locationType, locationData);
          onClose();
        }
      }
    });
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setSelectedLocation({ lat, lng });
    getLocationDetails(lat, lng);
  };

  const handleMarkerDrag = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setSelectedLocation({ lat, lng });
    getLocationDetails(lat, lng);
  };

  // Handle typing in search input
  //   const handleSearchInput = (e) => {
  //     const input = e.target.value;
  //     if (!input) {
  //       setSearchResults([]);
  //       return;
  //     }

  //     const service = new window.google.maps.places.AutocompleteService();
  //     service.getPlacePredictions({ input }, (predictions, status) => {
  //       if (status === window.google.maps.places.PlacesServiceStatus.OK) {
  //         setSearchResults(predictions);
  //       } else {
  //         setSearchResults([]);
  //       }
  //     });
  //   };

  // Handle selecting a search result
  //   const handleSelectPlace = (placeId, description) => {
  //     const service = new window.google.maps.places.PlacesService(mapRef.current);
  //     service.getDetails({ placeId }, (place, status) => {
  //       if (status === window.google.maps.places.PlacesServiceStatus.OK) {
  //         const lat = place.geometry.location.lat();
  //         const lng = place.geometry.location.lng();

  //         // Update selected location & map
  //         setSelectedLocation({ lat, lng });
  //         mapRef.current.panTo({ lat, lng });

  //         // Update input field instantly
  //         setSearchInput(description);

  //         // Fetch full details (city, state, country, etc.)
  //         getLocationDetails(lat, lng);

  //         // Hide dropdown list
  //         setSearchResults([]);
  //       }
  //     });
  //   };

  const handleSelectPlace = (placeId, description) => {
    const service = new window.google.maps.places.PlacesService(mapRef.current);
    service.getDetails({ placeId }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setSelectedLocation({ lat, lng });
        mapRef.current.panTo({ lat, lng });
        setSearchInput(description);
        getLocationDetails(lat, lng);
        setSearchResults([]);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (searchResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % searchResults.length);
      listRef.current?.scrollTo(0, (activeIndex + 1) * 48);
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
      listRef.current?.scrollTo(0, (activeIndex - 1) * 48);
    }
    if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const selectedPlace = searchResults[activeIndex];
      handleSelectPlace(selectedPlace.place_id, selectedPlace.description);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'fixed',
        top: 0,
        left: 0,
        bgcolor: 'white',
        zIndex: 9999
      }}
    >
      {loading || !isLoaded ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Search Bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: '54.5%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              width: '80%'
            }}
          >
            <TextField
              fullWidth
              autoFocus
              placeholder="Search location..."
              variant="outlined"
              value={searchInput}
              onChange={handleSearchInput}
              onKeyDown={handleKeyDown}
              InputProps={{
                sx: {
                  height: 40, // Reduced overall height
                  fontSize: 13,
                  padding: '0 10px',
                  borderRadius: '4px'
                },
                endAdornment: searchLoading && (
                  <CircularProgress size={18} sx={{ color: 'primary.main', mr: 1 }} />
                )
              }}
              sx={{
                bgcolor: 'white',
                borderRadius: '2px',
                '& fieldset': { borderColor: '#ccc' }
              }}
            />

            {/* Search Results List */}
            {searchResults.length > 0 && (
              <Paper
                ref={listRef}
                sx={{
                  position: 'absolute',
                  top: '45px',
                  width: '100%',
                  maxHeight: 250,
                  overflowY: 'auto',
                  boxShadow: '0px 4px 10px rgba(0,0,0,0.15)',
                  borderRadius: '8px',
                  zIndex: 2000
                }}
              >
                <List>
                  {searchResults.map((place, index) => (
                    <ListItem
                      key={place.place_id}
                      button
                      selected={activeIndex === index}
                      onClick={() => handleSelectPlace(place.place_id, place.description)}
                      sx={{
                        bgcolor: activeIndex === index ? 'primary.light' : 'white',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <ListItemText
                        primary={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: place.description.replace(
                                new RegExp(searchInput, 'gi'),
                                (match) => `<b>${match}</b>`
                              )
                            }}
                          />
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Google Map */}
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={selectedLocation || currentLocation}
            zoom={15}
            onClick={handleMapClick}
            onLoad={(map) => (mapRef.current = map)}
          >
            {selectedLocation && (
              <Marker
                position={selectedLocation}
                draggable
                animation={window.google.maps.Animation.BOUNCE} // Animate marker
                onDragEnd={handleMarkerDrag}
              />
            )}
          </GoogleMap>

          {/* Save & Close Buttons */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 2,
              zIndex: 1000
            }}
          >
            <Button
              variant="contained"
              color="success"
              onClick={() => getLocationDetails(selectedLocation.lat, selectedLocation.lng, true)}
            >
              Save Location
            </Button>
            <Button onClick={onClose} variant="contained" color="error">
              Close
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LocationPickerMap;
